// `autoplays`: Swift `Autoplay` / `AutoplayStep` (GroundTeam/Models/Automation.swift): follow-up sequences records are
// enrolled into. Decoding template `Autoplay(name: "", steps: [AutoplayStep()])`
// (GroundTeam/Sync/ResilientDecoding.swift:122): each stored step is merged over `AutoplayStep()`, so a step missing a
// key takes that default (a missing `id` becomes a fresh UUID, as on the phone), and a payload with no `steps` key
// reads as one default step. Both are mirrored.

import { AUTOPLAY_STEP_TYPES, AUTOPLAY_TRIGGERS, type AutoplayStepType, type AutoplayTrigger } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  DATE,
  INT,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  objectOf,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface AutoplayStepPayload extends Passthrough {
  id: string;
  type: AutoplayStepType;
  /** Days after the previous step (Int; 0 = same day). */
  delayDays: number;
  templateId?: string;
  instructions: string;
}

export const AUTOPLAY_STEP_KEYS = [
  'id',
  'type',
  'delayDays',
  'templateId',
  'instructions',
] as const satisfies readonly (keyof Known<AutoplayStepPayload>)[];

export const AUTOPLAY_STEP_FIELDS: FieldMap<AutoplayStepPayload> = {
  id: field.req(UUID, newId),
  type: field.req(enumOf('AutoplayStepType', AUTOPLAY_STEP_TYPES), () => 'call'),
  delayDays: field.req(INT, () => 1),
  templateId: field.opt(UUID),
  instructions: field.req(STRING, () => ''),
};

export const AUTOPLAY_STEP = objectOf<AutoplayStepPayload>('AutoplayStep', AUTOPLAY_STEP_FIELDS);

/** Reads one step as an element of `steps` (merged over the template `AutoplayStep()`). */
export function decodeAutoplayStep(raw: JsonObject): Decoded<AutoplayStepPayload> {
  return decodeModel(AUTOPLAY_STEP_FIELDS, raw, { hasId: false });
}

export type AutoplayStepInit = Partial<Known<AutoplayStepPayload>>;

/** `AutoplayStep()` with overrides. */
export function newAutoplayStep(init: AutoplayStepInit = {}): AutoplayStepPayload {
  return createModel(AUTOPLAY_STEP_FIELDS, { id: newId(), type: 'call', delayDays: 1, instructions: '' }, init, 'step');
}

export interface AutoplayPayload extends Passthrough {
  id: string;
  name: string;
  steps: AutoplayStepPayload[];
  trigger: AutoplayTrigger;
  triggerStageId?: string;
  triggerResultId?: string;
  isActive: boolean;
  createdAt: string;
}

export const AUTOPLAY_KEYS = [
  'id',
  'name',
  'steps',
  'trigger',
  'triggerStageId',
  'triggerResultId',
  'isActive',
  'createdAt',
] as const satisfies readonly (keyof Known<AutoplayPayload>)[];

export const AUTOPLAY_FIELDS: FieldMap<AutoplayPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  steps: field.req(arrayOf(AUTOPLAY_STEP), () => [newAutoplayStep()]),
  trigger: field.req(enumOf('AutoplayTrigger', AUTOPLAY_TRIGGERS), () => 'manual'),
  triggerStageId: field.opt(UUID),
  triggerResultId: field.opt(UUID),
  isActive: field.req(BOOL, () => true),
  createdAt: field.req(DATE, nowWire),
};

export function decodeAutoplay(raw: JsonObject, envelopeId?: string): Decoded<AutoplayPayload> {
  return decodeModel(AUTOPLAY_FIELDS, raw, {}, envelopeId);
}

export type AutoplayInit = Omit<Partial<Known<AutoplayPayload>>, 'steps'> & {
  name: string;
  /** Each step is completed with the AutoplayStep defaults. */
  steps?: AutoplayStepInit[];
};

export function newAutoplay(init: AutoplayInit): AutoplayPayload {
  const { steps, ...rest } = init;
  const defaults = {
    id: newId(),
    steps: (steps ?? []).map((s) => newAutoplayStep(s)),
    trigger: 'manual',
    isActive: true,
    createdAt: nowWire(),
  };
  return createModel(AUTOPLAY_FIELDS, defaults, rest, 'autoplays');
}
